import { useCallback, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EventsState } from '../../../store/types';
import { BrandliveEvent } from '../../../types/working-model';
import Modal from '../../general-ui/modal/modal';
import TextInput, { Validation } from '../../general-ui/text-input/text';
import { validateUrl, getPrefixUrl } from '../../../utils/utils';
import './event-settings-modal.scss';
import { CheckEventUrl, UpdatePublishedUrl } from '../../../connection/create-event';
import { showAlert } from '../../general-ui/alert/alert-service';
import WaitingIndicator from '../../general-ui/waiting-indicator/waiting-indicator';
import { updatePublishedUrl } from '../../../store/actions/admin/create-event';

interface Props {
	event: BrandliveEvent;
	open: boolean;
	setOpen: (open: boolean) => void;
}

export default function EventSettings({ event, open, setOpen }: Props): JSX.Element {
	const [loading, setLoading] = useState(false);
	const [urlValid, setUrlValid] = useState(true);
	const [errorMsg, setErrorMsg] = useState('');
	const [eventUrl, setEventUrl] = useState('');

	const {
		AuthReducer: {
			channels,
			user,
			token
		},
		CreateEventReducer: {
			selectedEvent,
			publishedUrl,
		}
	} = useSelector((state: EventsState) => state);

	const dispatch = useDispatch();

	const abortUrlCheck = useRef<AbortController | null>(null);

	const channel = useMemo(() => {
		return channels.find((channel) => channel.channel === user!.active_channel);
	}, [channels, user]);

	function handleEventUrl(e: React.ChangeEvent<HTMLInputElement>) {
		setEventUrl(e.target.value);
		if (validateUrl(`${getPrefixUrl(channel?.url ?? "")}/${event?.name.toLowerCase().replace(/[^0-9a-z]/g, '-') ?? ''}`))
			setUrlValid(true);
		else
			setUrlValid(false);
	}

	const checkUrl = useCallback(async (url: string) => {
		try {
			//use an abort controller here if someone types super fast, we don't want the 
			//fetch requests to arrive out of order and mess things up. 
			//so if the previous request is still in flight, abort the request and send a new one.
			if (abortUrlCheck.current) abortUrlCheck.current.abort();

			if (!url) {
				setUrlValid(false);
				setErrorMsg("Please enter a URL.");
				return;
			}

			const [controller, checkUrlPromise] = CheckEventUrl(token!, channel!.url, url, selectedEvent!.event!, selectedEvent!.channel);
			abortUrlCheck.current = controller;

			checkUrlPromise.then(({ exists }) => {
				if (exists) {
					setUrlValid(false);
					setErrorMsg("This URL already exists. Please enter a unique URL.");
				}
			}).catch(e => {
				console.error(e);
			});
		} catch (e) {
			console.error(e);
			showAlert({
				message: "Error Checking URL",
				description: "We were unable to check that event url. Please try again",
				type: "error"
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, selectedEvent]);

	const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		checkUrl((e.target as HTMLInputElement).value);
	}, [checkUrl]);

	async function updateEventSettings() {
		setLoading(true);
		try {
			await UpdatePublishedUrl(token!, selectedEvent!.uuid, eventUrl);
			dispatch(updatePublishedUrl(eventUrl));
			setLoading(false);
			setOpen(false);
		} catch (e) {
			setLoading(false);
			console.error(e);
			showAlert({
				message: "Error Updating URL",
				description: "We were unable to update that event url. Please try again",
				type: "error"
			});
		}
	}

	const closeEventSettings = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	return (
		<Modal
			open={open}
			onRequestClose={closeEventSettings}
			title={"Event Settings"}
			cancellable={true}
			closeable={false}
			footer={(
				<>
					<button onClick={() => setOpen(false)} style={{ marginRight: 16 }}>Cancel</button>
					<button onClick={updateEventSettings} disabled={!urlValid} className="primary">
						{
							loading ? <WaitingIndicator /> : "Update"
						}
					</button>
				</>
			)}
		>
			<div className="settings-modal-body">
				<div className="settings-modal-top">
					<div className="event-id">Event ID</div>
					<div className="subtext">Customize your built-in domain</div>
					<div className={urlValid ? "url-input valid " : "url-input invalid"}>
						{channel?.url ? (
							<TextInput
								label={"Event URL"}
								onChange={handleEventUrl}
								placeholder={""}
								prefix={`${getPrefixUrl(channel.url)}/`}
								defaultValue={publishedUrl || event?.name.toLowerCase().replace(/[^0-9a-z]/g, '-') || ''}
								onKeyUp={handleKeyUp}
								errorMessage={errorMsg}
								valid={urlValid ? Validation.normal : Validation.error}
							/>
						) : null}
						{urlValid && (
							<div className="url-valid">
								{"Available"}
							</div>
						)}
					</div>
				</div>
				{/* FUTURE DEV - hiding favicon upload */}
				{/* <div className="divider"></div>
				<div className="favicon-upload">
					Favicon
					<div className="subtext">Submit an image (png, jpg, svg, ico) at least 70x70. Your image should be 260x260 or more for optimal results</div>
					<button><Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Upload Favicon </button>
				</div> */}
			</div>
		</Modal>
	);
}